import React, { useState } from 'react';
import Alert from '../common/Alert.js';
import SelectField from '../common/SelectField.js';
import Button from '../common/Button.js';
import companySubscriptionService from '../../services/CompanySubscriptionService.js';
import { X, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RenewSubscriptionModal = ({ subscription, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    period_type: subscription?.period_type || 'one_month',
    custom_years: subscription?.custom_years || '',
    subscription_amount: subscription?.subscription_amount || '',
    notes: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        period_type: form.period_type,
        subscription_amount: parseFloat(form.subscription_amount),
        notes: form.notes || undefined,
      };
      if (form.period_type === 'custom_years') {
        payload.custom_years = parseInt(form.custom_years, 10);
      }
      const response = await companySubscriptionService.renew(subscription.id, payload);
      Alert.success(response.message || 'Subscription renewed successfully');
      onSuccess(response.data);
      onClose();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to renew subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-orange-600">
            <RefreshCw className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Renew Subscription</h2>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Renew subscription for <strong>{subscription.company_name}</strong> ({subscription.subscription_id}).
            {subscription.is_expired && ' A new period will start from today.'}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period Type</label>
              <SelectField variant="form"
                name="period_type"
                value={form.period_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
                required
              >
                <option value="seven_days">7 Days</option>
                <option value="one_month">1 Month</option>
                <option value="three_months">3 Months</option>
                <option value="six_months">6 Months</option>
                <option value="twelve_months">12 Months</option>
                <option value="custom_years">Custom Years</option>
              </SelectField>
            </div>

            {form.period_type === 'custom_years' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Years</label>
                <input
                  type="number"
                  name="custom_years"
                  min="1"
                  value={form.custom_years}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
                  required
                />
              </div>
            )}

            <div className={form.period_type === 'custom_years' ? '' : 'sm:col-span-2'}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Amount</label>
              <input
                type="number"
                name="subscription_amount"
                min="0.01"
                step="0.01"
                value={form.subscription_amount}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
              placeholder="Renewal notes"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="flex-1" loading={loading}>
              {loading ? 'Renewing...' : 'Renew Subscription'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RenewSubscriptionModal;
